import { useCallback, useEffect, useState } from "react";

import type { MyAcknowledgementRequestDto } from "@/generated/model";
import { generateClientId, offlineDb } from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

/**
 * What actually happened:
 *   - `synced` — the server took it.
 *   - `queued` — offline, so it waits in the sync queue.
 *   - `failed` — online, but the write did not land. The queue logs HTTP
 *     failures and moves on rather than throwing, so awaiting alone cannot tell
 *     this from success (the NAU-391 lesson).
 */
export type AcknowledgementSignOutcome = "synced" | "queued" | "failed";

interface UseOfflineAcknowledgementSignResult {
  sign: () => Promise<AcknowledgementSignOutcome>;
  markRead: () => Promise<void>;
  isPending: boolean;
  syncStatus: SyncStatus;
}

/** Priority band 4 — the same as an incident sign-off. */
const SIGN_PRIORITY = 4;

/**
 * A new, lowest band. Read is telemetry-grade: replaying it late costs a manager
 * one roster column, whereas a late signature costs a compliance record. Putting
 * it at 7 rather than 4 is a deliberate statement that it must never delay a
 * signature, a photo upload or a completion — and never be the item that burns
 * the five-retry budget and turns on the red "N items failed to sync" banner for
 * something nobody is waiting on.
 */
const READ_PRIORITY = 7;

/**
 * Records a read and a signature so both work offline (NAU-410 AC35).
 *
 * Follows `use-offline-hot-holding-alert-ack`, with one real difference: there
 * are two facts to record against one request, held in one row with two
 * timestamps and two sync states.
 */
export function useOfflineAcknowledgementSign(
  requestId: string,
  nurseryId: string | null,
  userId: string | null,
): UseOfflineAcknowledgementSignResult {
  const [isPending, setIsPending] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    pendingCount: 0,
    errorCount: 0,
    isSyncing: false,
    isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  });

  useEffect(() => {
    syncService.getStatus().then(setSyncStatus);
    const unsubscribe = syncService.subscribe(setSyncStatus);
    return () => {
      unsubscribe();
    };
  }, []);

  /**
   * Fire-and-forget. Read is not a user action, so a failure here is never
   * surfaced — a toast about it would be inexplicable to the staff member.
   */
  const markRead = useCallback(async () => {
    const existing = await offlineDb.acknowledgementAcks.get(requestId);
    if (existing?.readAt) {
      return;
    }
    const readAt = new Date().toISOString();
    await offlineDb.acknowledgementAcks.put({
      ...existing,
      id: requestId,
      queuedAt: existing?.queuedAt ?? readAt,
      readAt,
      readSyncState: "pending",
    });

    const alreadyQueued = await offlineDb.syncQueue
      .filter(
        (i) => i.type === "acknowledgementRead" && i.entityId === requestId,
      )
      .count();
    if (alreadyQueued === 0) {
      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "acknowledgementRead",
        entityId: requestId,
        priority: READ_PRIORITY,
        createdAt: Date.now(),
        attempts: 0,
      });
    }

    if (navigator.onLine) {
      await syncService.immediateSync();
    }
  }, [requestId]);

  const sign = useCallback(async () => {
    setIsPending(true);
    try {
      const existing = await offlineDb.acknowledgementAcks.get(requestId);
      const signedAt = new Date().toISOString();
      await offlineDb.acknowledgementAcks.put({
        ...existing,
        id: requestId,
        queuedAt: existing?.queuedAt ?? signedAt,
        // Signing implies reading. Without this the local row would be
        // self-inconsistent — signed, never read — and the server backfills the
        // same way, so keeping them in step avoids a confusing intermediate.
        readAt: existing?.readAt ?? signedAt,
        readSyncState: existing?.readSyncState ?? "pending",
        signedAt,
        signSyncState: "pending",
      });

      // Optimistically drop the row from the cached To do list so it disappears
      // immediately, offline or on. This is NOT redundant with query
      // invalidation: the To do list is a Dexie hook, not a React Query, so the
      // invalidation helper never reaches it.
      //
      // Keep what was removed: if the signature exhausts its retries the sync
      // worker puts it back, so the staff member sees it failed rather than
      // believing they signed something that never saved.
      const removed = await removeFromCachedList(requestId, nurseryId, userId);
      if (removed.entry && removed.key) {
        await offlineDb.acknowledgementAcks.update(requestId, {
          removedListEntry: removed.entry,
          removedListKey: removed.key,
        });
      }

      const alreadyQueued = await offlineDb.syncQueue
        .filter(
          (i) => i.type === "acknowledgementSign" && i.entityId === requestId,
        )
        .count();
      if (alreadyQueued === 0) {
        await offlineDb.syncQueue.add({
          id: generateClientId(),
          type: "acknowledgementSign",
          entityId: requestId,
          priority: SIGN_PRIORITY,
          createdAt: Date.now(),
          attempts: 0,
        });
      }

      if (!navigator.onLine) {
        return "queued";
      }

      await syncService.immediateSync();

      // The queue swallows a failed replay, records the attempt and carries on,
      // so immediateSync resolving proves nothing. The stored row is the only
      // honest signal that the server took it.
      const ack = await offlineDb.acknowledgementAcks.get(requestId);
      return ack?.signSyncState === "synced" ? "synced" : "failed";
    } finally {
      setIsPending(false);
    }
  }, [requestId, nurseryId, userId]);

  return { sign, markRead, isPending, syncStatus };
}

/** Cache key: per-user, never per-nursery — see OfflineAcknowledgementRequests. */
export function myAcknowledgementsCacheKey(
  nurseryId: string,
  userId: string,
): string {
  return `my-acknowledgements-${nurseryId}-${userId}`;
}

/**
 * Drops the row from the cached To do list, returning what was removed so the
 * sync worker can restore it if the signature never reaches the server.
 */
async function removeFromCachedList(
  requestId: string,
  nurseryId: string | null,
  userId: string | null,
): Promise<{ entry?: MyAcknowledgementRequestDto; key?: string }> {
  if (!nurseryId || !userId) {
    return {};
  }
  const key = myAcknowledgementsCacheKey(nurseryId, userId);
  const cached = await offlineDb.acknowledgementRequests.get(key);
  if (!cached) {
    return {};
  }
  const requests = (cached.requests as MyAcknowledgementRequestDto[]) ?? [];
  const entry = requests.find((request) => request.requestId === requestId);
  await offlineDb.acknowledgementRequests.put({
    ...cached,
    requests: requests.filter((request) => request.requestId !== requestId),
  });
  return { entry, key };
}
